import { useCallback, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faPlug,
  faKey,
  faPen,
  faUnlink,
  faCloudArrowUp,
  faArrowsRotate,
  faCircleInfo,
  faChevronDown,
  faChevronUp,
  faClock,
  faList,
  faUpload,
  faTriangleExclamation,
} from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import { formatCurrencyCentValue, formatDate, formatRFCDate } from '@/utils/helpers';
import { showSuccessToast, handleAPIError } from '@/utils/toasts';
import type {
  JournalIBAssetCategory,
  JournalIBConfig,
  JournalIBExecution,
  JournalIBImportResult,
  JournalIBSyncRun,
  JournalIBSyncStatus,
  JournalIBSyncTrigger,
  JournalIBUnknownSymbolStat,
} from '@/models';
import type { UpdateJournalIBConfigRequest } from '@/modelsAPI';
import Badge from '@/components/ui/Badge';
import type { BadgeColor } from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import DataTable from '@/components/ui/DataTable';
import type { DataTableColumn } from '@/components/ui/DataTable';
import EmptyState from '@/components/ui/EmptyState';
import ErrorState from '@/components/ui/ErrorState';
import Modal from '@/components/ui/Modal';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import StatCard from '@/components/ui/StatCard';
import { JOURNAL_FIELD_CLASS, JOURNAL_LABEL_CLASS } from '@/components/journal/journalHelpers';
import { useJournalResource } from '@/components/journal/useJournalResource';

const RUN_LIMIT = 10;
const EXECUTION_PAGE_SIZE = 25;
const MAX_UPLOAD_BYTES = 16 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = ['.xml', '.csv'];

// Mirrors the backend validation in api/handler/journalIB.go.
const TOKEN_PATTERN = /^\d{23,24}$/;
const QUERY_ID_PATTERN = /^\d+$/;

const INTEGER_FORMAT = new Intl.NumberFormat('de-DE');
const SIGNED_INTEGER_FORMAT = new Intl.NumberFormat('de-DE', { signDisplay: 'exceptZero' });

const SYNC_STATUS_LABELS: Record<JournalIBSyncStatus, string> = {
  NOT_CONFIGURED: 'Nicht eingerichtet',
  RUNNING: 'Läuft',
  SUCCESS: 'Erfolgreich',
  NO_DATA: 'Keine neuen Daten',
  RATE_LIMITED: 'Zu viele Anfragen',
  UNAVAILABLE: 'Noch nicht verfügbar',
  FAILED: 'Fehlgeschlagen',
};

const SYNC_STATUS_COLORS: Record<JournalIBSyncStatus, BadgeColor> = {
  NOT_CONFIGURED: 'gray',
  RUNNING: 'blue',
  SUCCESS: 'green',
  NO_DATA: 'lime',
  RATE_LIMITED: 'yellow',
  UNAVAILABLE: 'yellow',
  FAILED: 'red',
};

const TRIGGER_LABELS: Record<JournalIBSyncTrigger, string> = {
  MANUAL: 'Manuell',
  JOB: 'Automatisch',
  FILE: 'Datei-Import',
};

const ASSET_CATEGORY_LABELS: Record<JournalIBAssetCategory, string> = {
  OPT: 'Option',
  STK: 'Aktie',
  OTHER: 'Sonstiges',
};

function formatPeriod(from: string | null, to: string | null): string {
  if (from === null && to === null) return '—';
  if (from === null) return `bis ${formatRFCDate(to)}`;
  if (to === null) return `ab ${formatRFCDate(from)}`;
  if (from === to) return formatRFCDate(from);
  return `${formatRFCDate(from)} – ${formatRFCDate(to)}`;
}

/** Options carry their contract in three separate fields - IB's own symbol column is unreadable. */
function executionSymbolLabel(execution: JournalIBExecution): string {
  if (execution.assetCategory !== 'OPT' || execution.right === null) return execution.symbol;
  const strike = execution.strike === null ? '' : ` ${formatCurrencyCentValue(execution.strike, execution.currency)}`;
  const expiry = execution.expiry === null ? '' : ` ${formatRFCDate(execution.expiry)}`;
  return `${execution.underlyingSymbol} ${execution.right}${strike}${expiry}`;
}

function fileExtension(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot).toLowerCase();
}

/** Rejects what the backend would reject anyway, but without a 16 MB round trip. */
function validateStatementFile(file: File): string | null {
  if (!ACCEPTED_EXTENSIONS.includes(fileExtension(file.name))) {
    return 'Nur .xml- und .csv-Dateien können importiert werden.';
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return 'Die Datei ist größer als 16 MB.';
  }
  return null;
}

interface SwitchProps {
  checked: boolean;
  disabled: boolean;
  label: string;
  onChange: () => void;
}

/** ToggleButton's look, but with the switch semantics a settings toggle needs. */
function Switch({ checked, disabled, label, onChange }: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-800 ${
        checked ? 'bg-green-600 dark:bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

function ImportResultPanel({ result }: { result: JournalIBImportResult }) {
  const run = result.run;
  const accounts = result.discoveredAccounts ?? [];

  return (
    <div className="mt-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge color={SYNC_STATUS_COLORS[run.status]}>{SYNC_STATUS_LABELS[run.status]}</Badge>
        <span className="text-sm text-gray-500 dark:text-gray-400">Zeitraum: {formatPeriod(run.fromDate, run.toDate)}</span>
      </div>

      {run.message !== '' && <p className="text-sm text-gray-700 dark:text-gray-300">{run.message}</p>}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <StatCard label="Abgerufen" value={INTEGER_FORMAT.format(run.executionsFetched)} sub="Ausführungen" />
        <StatCard label="Neu importiert" value={INTEGER_FORMAT.format(run.executionsNew)} sub="Ausführungen" />
        <StatCard label="Neue Optionen" value={INTEGER_FORMAT.format(run.optionTradesNew)} sub="Trades" />
        <StatCard label="Aktualisiert" value={INTEGER_FORMAT.format(run.optionTradesUpdated)} sub="Optionen" />
        <StatCard label="Neue Aktien" value={INTEGER_FORMAT.format(run.stockPositionsNew)} sub="Positionen" />
      </div>

      {accounts.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <span>Erkannte Konten:</span>
          {accounts.map((account) => (
            <Badge key={account} color="blue">{account}</Badge>
          ))}
        </div>
      )}

      {run.unknownSymbols.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <span>Ohne Symbol übersprungen:</span>
          {run.unknownSymbols.map((unknown) => (
            <Badge key={unknown.ticker} color="orange">
              {unknown.ticker} ({INTEGER_FORMAT.format(unknown.executionCount)})
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

function Anleitung() {
  const [open, setOpen] = useState(false);

  return (
    <Card
      title="Anleitung: IBKR einrichten"
      icon={faCircleInfo}
      padded={false}
      actions={
        <Button
          variant="secondary"
          size="sm"
          icon={open ? faChevronUp : faChevronDown}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? 'Einklappen' : 'Schritt für Schritt'}
        </Button>
      }
    >
      {open && (
        <div className="p-4 space-y-6 text-sm text-gray-700 dark:text-gray-300">
          <p>
            Du brauchst zwei Angaben aus deinem eigenen IBKR-Konto: einen Flex-Web-Service-Token und die ID einer
            Flex Query. Beides legst du einmalig in der IBKR-Kontoverwaltung an, wir speichern es verschlüsselt.
          </p>

          <section>
            <h3 className="font-semibold text-gray-900 dark:text-white">1. Flex Web Service aktivieren</h3>
            <ol className="mt-2 list-decimal pl-5 space-y-1">
              <li>Melde dich im IBKR Client Portal an (nicht in der Trader Workstation).</li>
              <li>
                Öffne <span className="font-medium">Performance &amp; Berichte → Flex Queries</span>
                {' '}(englische Oberfläche: <span className="font-medium">Performance &amp; Reports → Flex Queries</span>).
              </li>
              <li>
                Klicke im Bereich <span className="font-medium">Flex-Web-Service-Konfiguration</span> rechts auf das
                Zahnrad und setze den Haken bei <span className="font-medium">Flex-Web-Service-Status</span>.
              </li>
              <li>
                Kopiere den angezeigten <span className="font-medium">aktiven Prüfcode</span> — das ist dein Token,
                eine reine Ziffernfolge mit 23 oder 24 Stellen. Er ist standardmäßig ein Jahr gültig; danach musst du
                ihn hier neu hinterlegen.
              </li>
            </ol>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900 dark:text-white">2. Flex Query für Trades anlegen</h3>
            <p className="mt-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-amber-800 dark:text-amber-200">
              Wichtig: Es muss eine <span className="font-semibold">Handelsbestätigungs-Flex-Query</span> sein
              (englisch <span className="font-semibold">Trade Confirmation Flex Query</span>) — nicht eine
              Kontoumsatz- bzw. Activity-Flex-Query. Das ist mit Abstand der häufigste Fehler: eine Kontoumsatz-Query
              liefert zwar eine Datei, aber keine Ausführungen, die wir lesen können.
            </p>
            <ol className="mt-2 list-decimal pl-5 space-y-1">
              <li>
                Klicke auf derselben Seite im Block <span className="font-medium">Handelsbestätigungs-Flex-Query</span>{' '}
                auf das Plus-Symbol.
              </li>
              <li>Vergib einen Namen, z.B. „BolzAI Journal“.</li>
              <li>
                Öffne den Abschnitt <span className="font-medium">Handelsbestätigung</span> und wähle die Felder der
                Ausführung aus. Gebraucht werden mindestens: Account ID, Trade ID, Asset Class, Symbol, Underlying
                Symbol, Put/Call, Strike, Expiry, Multiplier, Quantity, Price, Commission, Currency, Trade Date,
                Date/Time, Open/Close und Notes/Codes.
              </li>
              <li>
                Stelle in der Zustellungskonfiguration <span className="font-medium">Format: XML</span> und{' '}
                <span className="font-medium">Datumsformat: yyyyMMdd</span> ein (Zeitformat HHmmss). Andere
                Datumsformate können wir nicht zuverlässig lesen.
              </li>
              <li>
                Wähle als Zeitraum etwas, das deine Historie abdeckt — für den ersten Import z.B. die letzten 365
                Tage, danach genügt „Heute“ bzw. „Letzter Geschäftstag“.
              </li>
              <li>Speichern. In der Übersicht steht neben der Query eine rein numerische Query ID — die kopierst du.</li>
            </ol>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900 dark:text-white">3. Hier eintragen</h3>
            <p className="mt-2">
              Trage Token und Query ID oben unter „Einrichten“ ein und starte anschließend „Jetzt synchronisieren“.
              Wenn du den Flex Web Service (noch) nicht aktivieren möchtest, kannst du die Flex Query auch von Hand im
              Client Portal ausführen, die XML-Datei herunterladen und unten unter „Datei-Import“ hochladen — dafür
              wird kein Token gebraucht.
            </p>
          </section>

          <section>
            <h3 className="font-semibold text-gray-900 dark:text-white">Wenn ein Import nichts liefert</h3>
            <ul className="mt-2 list-disc pl-5 space-y-1">
              <li>
                <span className="font-medium">Keine neuen Daten:</span> Alles aus dem Zeitraum ist bereits importiert.
                Das ist kein Fehler — ein zweiter Import derselben Ausführungen ändert nichts.
              </li>
              <li>
                <span className="font-medium">Noch nicht verfügbar:</span> IBKR erstellt die Abrechnung des Vortags
                erst im Laufe des Morgens. Später erneut versuchen.
              </li>
              <li>
                <span className="font-medium">Zu viele Anfragen:</span> IBKR begrenzt die Abrufe pro Query. Ein paar
                Minuten warten, dann geht es wieder.
              </li>
              <li>
                <span className="font-medium">Fehlgeschlagen:</span> Meist ein abgelaufener Token, eine falsche Query
                ID oder eine Activity- statt Trade-Confirmation-Query.
              </li>
            </ul>
          </section>
        </div>
      )}
    </Card>
  );
}

interface ConfigModalProps {
  config: JournalIBConfig;
  onClose: () => void;
  onSaved: () => void;
}

function ConfigModal({ config, onClose, onSaved }: ConfigModalProps) {
  const [token, setToken] = useState('');
  const [queryTrades, setQueryTrades] = useState(config.flexQueryTrades);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  const trimmedToken = token.trim();
  const trimmedQuery = queryTrades.trim();

  const tokenError = ((): string | null => {
    if (trimmedToken === '') {
      return config.tokenSet ? null : 'Ein Flex-Token wird benötigt.';
    }
    if (!TOKEN_PATTERN.test(trimmedToken)) return 'Der Token besteht aus 23 oder 24 Ziffern.';
    return null;
  })();

  const queryError = ((): string | null => {
    if (trimmedQuery === '') return 'Eine Flex Query ID wird benötigt.';
    if (!QUERY_ID_PATTERN.test(trimmedQuery)) return 'Die Query ID besteht nur aus Ziffern.';
    return null;
  })();

  const invalid = tokenError !== null || queryError !== null;

  const handleSave = async () => {
    setTouched(true);
    if (invalid) return;

    setSaving(true);
    try {
      const request: UpdateJournalIBConfigRequest = { flexQueryTrades: trimmedQuery };
      // An absent token keeps the stored one - never send an empty string, that would clear it.
      if (trimmedToken !== '') request.token = trimmedToken;
      await APIRoutes.updateJournalIBConfig(request);
      showSuccessToast('IB-Verbindung gespeichert');
      onSaved();
      onClose();
    } catch (err) {
      handleAPIError(err, 'IB-Verbindung konnte nicht gespeichert werden');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={config.tokenSet ? 'IB-Verbindung bearbeiten' : 'IB-Verbindung einrichten'}
      icon={<FontAwesomeIcon icon={faKey} className="w-5 h-5 text-blue-600 dark:text-blue-400" />}
      maxWidth="lg"
      closeDisabled={saving}
    >
      <div className="p-6 space-y-4">
        <div>
          <label className={JOURNAL_LABEL_CLASS} htmlFor="ib-token">Flex-Token</label>
          <input
            id="ib-token"
            type="password"
            autoComplete="off"
            inputMode="numeric"
            className={`${JOURNAL_FIELD_CLASS} w-full font-mono disabled:opacity-60`}
            value={token}
            disabled={saving}
            placeholder={config.tokenSet ? '•••••••••••••••••••••••' : '23 oder 24 Ziffern'}
            onChange={(e) => setToken(e.target.value)}
            onBlur={() => setTouched(true)}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {config.tokenSet
              ? 'Es ist bereits ein Token hinterlegt. Leer lassen, um ihn zu behalten.'
              : 'Der Token aus der Flex-Web-Service-Konfiguration im IBKR Client Portal.'}
          </p>
          {touched && tokenError !== null && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">{tokenError}</p>
          )}
        </div>

        <div>
          <label className={JOURNAL_LABEL_CLASS} htmlFor="ib-query-trades">Flex Query ID – Trades</label>
          <input
            id="ib-query-trades"
            type="text"
            autoComplete="off"
            inputMode="numeric"
            className={`${JOURNAL_FIELD_CLASS} w-full font-mono disabled:opacity-60`}
            value={queryTrades}
            disabled={saving}
            placeholder="z.B. 1234567"
            onChange={(e) => setQueryTrades(e.target.value)}
            onBlur={() => setTouched(true)}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Die ID deiner Trade Confirmation Flex Query (Format XML, Datumsformat yyyyMMdd).
          </p>
          {touched && queryError !== null && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">{queryError}</p>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" disabled={saving} onClick={onClose}>Abbrechen</Button>
          <Button loading={saving} disabled={touched && invalid} onClick={handleSave}>Speichern</Button>
        </div>
      </div>
    </Modal>
  );
}

export default function JournalImportPage() {
  const status = useJournalResource(
    async () => {
      const [configResp, runsResp, unknownResp] = await Promise.all([
        APIRoutes.getJournalIBConfig(),
        APIRoutes.listJournalIBSyncRuns({ limit: RUN_LIMIT }),
        APIRoutes.listJournalIBUnknownSymbols(),
      ]);
      return { config: configResp.config, runs: runsResp.runs, unknownSymbols: unknownResp.unknownSymbols };
    },
    [],
    'Fehler beim Laden der IB-Verbindung',
  );

  const [executionPage, setExecutionPage] = useState(1);
  const executions = useJournalResource(
    () => APIRoutes.listJournalIBExecutions({
      limit: EXECUTION_PAGE_SIZE,
      next: executionPage > 1 ? `$${executionPage}` : undefined,
    }),
    [executionPage],
    'Fehler beim Laden der Ausführungen',
  );

  const [configOpen, setConfigOpen] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [autoSyncSaving, setAutoSyncSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<JournalIBImportResult | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<JournalIBImportResult | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reloadStatus = status.reload;
  const reloadExecutions = executions.reload;
  const reloadAll = useCallback(() => {
    reloadStatus();
    reloadExecutions();
  }, [reloadStatus, reloadExecutions]);

  const config = status.data?.config ?? null;
  const runs: JournalIBSyncRun[] = status.data?.runs ?? [];
  const unknownSymbols: JournalIBUnknownSymbolStat[] = status.data?.unknownSymbols ?? [];
  const executionRows: JournalIBExecution[] = executions.data?.executions ?? [];
  const executionTotal = executions.data?.total ?? 0;

  const handleToggleAutoSync = async () => {
    if (config === null) return;
    setAutoSyncSaving(true);
    try {
      await APIRoutes.updateJournalIBConfig({ autoSync: !config.autoSync });
      showSuccessToast(config.autoSync ? 'Automatischer Abgleich deaktiviert' : 'Automatischer Abgleich aktiviert');
      reloadStatus();
    } catch (err) {
      handleAPIError(err, 'Einstellung konnte nicht gespeichert werden');
    } finally {
      setAutoSyncSaving(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await APIRoutes.disconnectJournalIB();
      showSuccessToast('IB-Verbindung getrennt');
      setDisconnectOpen(false);
      setSyncResult(null);
      setUploadResult(null);
      setUploadError(null);
      reloadAll();
    } catch (err) {
      handleAPIError(err, 'Verbindung konnte nicht getrennt werden');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSync = async () => {
    if (syncing || uploading) return;
    setSyncing(true);
    // Only ever show the panel of the run that is actually current.
    setSyncResult(null);
    setUploadResult(null);
    setUploadError(null);
    try {
      const result = await APIRoutes.syncJournalIB();
      setSyncResult(result);
      showSuccessToast('Abgleich abgeschlossen');
      reloadAll();
    } catch (err) {
      handleAPIError(err, 'Der Abgleich mit Interactive Brokers ist fehlgeschlagen');
    } finally {
      setSyncing(false);
    }
  };

  const handleFile = async (file: File) => {
    if (uploading || syncing) return;
    const validationError = validateStatementFile(file);
    if (validationError !== null) {
      setUploadError(validationError);
      setUploadResult(null);
      // Reset the picker so re-choosing the same file fires onChange again.
      if (fileInputRef.current !== null) fileInputRef.current.value = '';
      return;
    }

    setUploadError(null);
    setUploadResult(null);
    setSyncResult(null);
    setUploading(true);
    try {
      const result = await APIRoutes.importJournalIBFile(file);
      setUploadResult(result);
      showSuccessToast('Datei importiert');
      reloadAll();
    } catch (err) {
      handleAPIError(err, 'Die Datei konnte nicht importiert werden');
    } finally {
      setUploading(false);
      if (fileInputRef.current !== null) fileInputRef.current.value = '';
    }
  };

  const runColumns: DataTableColumn<JournalIBSyncRun>[] = [
    {
      key: 'startedAt',
      header: 'Start',
      primary: true,
      render: (run) => <span className="font-medium text-gray-900 dark:text-white">{formatDate(run.startedAt)}</span>,
    },
    {
      key: 'trigger',
      header: 'Auslöser',
      render: (run) => (
        <span>
          {TRIGGER_LABELS[run.trigger]}
          {run.trigger === 'FILE' && run.fileName !== '' && (
            <span className="block text-xs text-gray-500 dark:text-gray-400 break-all">{run.fileName}</span>
          )}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (run) => <Badge color={SYNC_STATUS_COLORS[run.status]}>{SYNC_STATUS_LABELS[run.status]}</Badge>,
    },
    { key: 'period', header: 'Zeitraum', render: (run) => formatPeriod(run.fromDate, run.toDate) },
    { key: 'fetched', header: 'Abgerufen', align: 'right', render: (run) => INTEGER_FORMAT.format(run.executionsFetched) },
    { key: 'new', header: 'Neu', align: 'right', render: (run) => INTEGER_FORMAT.format(run.executionsNew) },
    { key: 'optionsNew', header: 'Optionen neu', align: 'right', render: (run) => INTEGER_FORMAT.format(run.optionTradesNew) },
    { key: 'optionsUpdated', header: 'Optionen akt.', align: 'right', render: (run) => INTEGER_FORMAT.format(run.optionTradesUpdated) },
    { key: 'stocksNew', header: 'Aktien neu', align: 'right', render: (run) => INTEGER_FORMAT.format(run.stockPositionsNew) },
    {
      key: 'unknownSymbols',
      header: 'Ohne Symbol',
      align: 'right',
      hideOnMobile: true,
      render: (run) => run.unknownSymbols.length === 0
        ? '—'
        : (
          <Badge color="orange">
            {run.unknownSymbols.map((unknown) => unknown.ticker).join(', ')}
          </Badge>
        ),
    },
    {
      key: 'message',
      header: 'Meldung',
      render: (run) => <span className="text-gray-500 dark:text-gray-400">{run.message === '' ? '—' : run.message}</span>,
    },
  ];

  const executionColumns: DataTableColumn<JournalIBExecution>[] = [
    {
      key: 'symbol',
      header: 'Kontrakt',
      primary: true,
      render: (e) => <span className="font-medium text-gray-900 dark:text-white">{executionSymbolLabel(e)}</span>,
    },
    { key: 'tradeDate', header: 'Handelstag', render: (e) => formatRFCDate(e.tradeDate) },
    { key: 'account', header: 'Konto', render: (e) => <span className="font-mono text-xs">{e.accountID}</span> },
    { key: 'category', header: 'Art', render: (e) => ASSET_CATEGORY_LABELS[e.assetCategory] },
    {
      key: 'quantity',
      header: 'Menge',
      align: 'right',
      render: (e) => (
        <span className={e.quantity < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}>
          {SIGNED_INTEGER_FORMAT.format(e.quantity)}
        </span>
      ),
    },
    { key: 'price', header: 'Preis', align: 'right', render: (e) => formatCurrencyCentValue(e.price, e.currency) },
    { key: 'commission', header: 'Gebühr', align: 'right', render: (e) => formatCurrencyCentValue(e.commission, e.currency) },
    {
      key: 'codes',
      header: 'IB-Codes',
      render: (e) =>
        e.codes.length === 0 ? (
          <span className="text-gray-400 dark:text-gray-500">—</span>
        ) : (
          <span className="flex flex-wrap gap-1">
            {e.codes.map((code) => (
              <Badge key={code} color="gray">{code}</Badge>
            ))}
          </span>
        ),
    },
  ];

  if (status.loading && status.data === null) {
    return (
      <div className="text-center py-16" role="status" aria-label="Lädt">
        <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (status.error !== null || config === null) {
    return <ErrorState message={status.error ?? 'Fehler beim Laden der IB-Verbindung'} onRetry={reloadStatus} />;
  }

  const nothingSetUp = config.configured === false && config.tokenSet === false && config.flexQueryTrades === '' && runs.length === 0;

  return (
    <div className="space-y-6">
      <Card
        title="Verbindungsstatus"
        icon={faPlug}
        actions={
          <>
            <Button
              variant="secondary"
              size="sm"
              icon={config.tokenSet ? faPen : faKey}
              onClick={() => setConfigOpen(true)}
            >
              {config.tokenSet ? 'Bearbeiten' : 'Einrichten'}
            </Button>
            {(config.tokenSet || config.flexQueryTrades !== '') && (
              <Button variant="ghost" size="sm" icon={faUnlink} onClick={() => setDisconnectOpen(true)}>
                Verbindung trennen
              </Button>
            )}
          </>
        }
      >
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Verbindung</dt>
            <dd className="mt-1">
              <Badge color={config.configured ? 'green' : 'gray'}>
                {config.configured ? 'Vollständig eingerichtet' : 'Nicht vollständig eingerichtet'}
              </Badge>
            </dd>
          </div>

          <div>
            <dt className="text-gray-500 dark:text-gray-400">Flex-Token</dt>
            <dd className="mt-1">
              <Badge color={config.tokenSet ? 'green' : 'gray'}>{config.tokenSet ? 'hinterlegt' : 'nicht hinterlegt'}</Badge>
            </dd>
          </div>

          <div>
            <dt className="text-gray-500 dark:text-gray-400">Flex Query ID – Trades</dt>
            <dd className="mt-1 font-mono text-gray-900 dark:text-white break-all">
              {config.flexQueryTrades === '' ? '—' : config.flexQueryTrades}
            </dd>
          </div>

          <div>
            <dt className="text-gray-500 dark:text-gray-400">Erkannte Konten</dt>
            <dd className="mt-1 flex flex-wrap gap-1">
              {config.accountIDs.length === 0 ? (
                <span className="text-gray-500 dark:text-gray-400">Noch keine — sie werden beim Import erkannt.</span>
              ) : (
                config.accountIDs.map((account) => (
                  <Badge key={account} color="blue">{account}</Badge>
                ))
              )}
            </dd>
          </div>

          <div>
            <dt className="text-gray-500 dark:text-gray-400">Automatischer Abgleich</dt>
            <dd className="mt-1 flex items-center gap-3">
              <Switch
                checked={config.autoSync}
                disabled={autoSyncSaving}
                label="Trades täglich automatisch bei Interactive Brokers abholen"
                onChange={() => void handleToggleAutoSync()}
              />
              <span className="text-gray-700 dark:text-gray-300">
                {config.autoSync ? 'Täglich automatisch' : 'Nur manuell'}
              </span>
            </dd>
          </div>

          <div>
            <dt className="text-gray-500 dark:text-gray-400">Letzter Abgleich</dt>
            <dd className="mt-1 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-gray-900 dark:text-white">
                  {config.lastSyncAt === null ? 'Noch nie' : formatDate(config.lastSyncAt)}
                </span>
                <Badge color={SYNC_STATUS_COLORS[config.lastSyncStatus]}>
                  {SYNC_STATUS_LABELS[config.lastSyncStatus]}
                </Badge>
              </div>
              {config.lastSyncMessage !== '' && (
                <p className="text-gray-500 dark:text-gray-400">{config.lastSyncMessage}</p>
              )}
            </dd>
          </div>
        </dl>
      </Card>

      {unknownSymbols.length > 0 && (
        <Card title="Underlyings ohne Symbol" icon={faTriangleExclamation}>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Für diese Basiswerte liegen importierte Ausführungen vor, aber es gibt kein passendes Symbol.
            Die Ausführungen sind gespeichert — sobald das Symbol angelegt ist, erzeugt der nächste Import
            die zugehörigen Trades und Positionen rückwirkend. Melde dich dafür beim Support.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {unknownSymbols.map((unknown) => (
              <Badge key={unknown.ticker} color="orange">
                {unknown.ticker} · {INTEGER_FORMAT.format(unknown.executionCount)}
                {unknown.executionCount === 1 ? ' Ausführung' : ' Ausführungen'}
                {' '}· zuletzt {formatRFCDate(unknown.lastTradeDate)}
              </Badge>
            ))}
          </div>
        </Card>
      )}

      {nothingSetUp && (
        <Card>
          <EmptyState
            icon={faUpload}
            title="Noch keine Trades importiert"
            description="Es gibt zwei Wege: Richte die IBKR-Verbindung mit Token und Flex Query ID ein, dann holen wir deine Ausführungen selbst. Oder lade eine bereits heruntergeladene Flex-Datei hoch — dafür brauchst du keinen Token."
            action={
              <div className="flex flex-wrap justify-center gap-2">
                <Button icon={faKey} onClick={() => setConfigOpen(true)}>Einrichten</Button>
                <Button variant="secondary" icon={faCloudArrowUp} disabled={uploading || syncing} onClick={() => fileInputRef.current?.click()}>
                  Datei hochladen
                </Button>
              </div>
            }
          />
        </Card>
      )}

      <Anleitung />

      <Card title="Jetzt synchronisieren" icon={faArrowsRotate}>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Wir fordern deine aktuelle Trade-Abrechnung direkt bei Interactive Brokers an und importieren sie.
          <span className="font-medium text-gray-900 dark:text-white">
            {' '}Das dauert etwa 30 Sekunden
          </span>{' '}
          — IBKR braucht diese Zeit zwischen Anforderung und Auslieferung der Abrechnung. Lass die Seite so lange offen.
        </p>

        {config.configured === false && (
          <p className="mt-3 text-sm text-amber-700 dark:text-amber-300">
            Dafür müssen Flex-Token und Flex Query ID hinterlegt sein. Nutze so lange den Datei-Import weiter unten.
          </p>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button
            icon={faArrowsRotate}
            loading={syncing}
            disabled={config.configured === false || uploading}
            onClick={handleSync}
          >
            {syncing ? 'Abgleich läuft …' : 'Jetzt synchronisieren'}
          </Button>
          {syncing && (
            <span className="text-sm text-gray-500 dark:text-gray-400" role="status">
              Warte auf Interactive Brokers … das kann bis zu 30 Sekunden dauern.
            </span>
          )}
        </div>

        {syncResult !== null && <ImportResultPanel result={syncResult} />}
      </Card>

      <Card title="Datei-Import" icon={faCloudArrowUp}>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Lade eine Flex-Abrechnung hoch, die du selbst im IBKR Client Portal heruntergeladen hast. Dieser Weg braucht
          weder Token noch Flex Web Service — ideal, wenn du den Dienst noch nicht aktiviert hast.
        </p>

        <input
          ref={fileInputRef}
          type="file"
          accept=".xml,.csv"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file !== undefined) void handleFile(file);
          }}
        />

        <button
          type="button"
          disabled={uploading || syncing}
          aria-label="Flex-Abrechnung auswählen oder hierher ziehen"
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragActive(false);
            const file = e.dataTransfer.files[0];
            if (file !== undefined) void handleFile(file);
          }}
          className={`mt-4 w-full rounded-lg border-2 border-dashed px-4 py-10 text-center transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900 ${
            dragActive
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
              : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500'
          }`}
        >
          <FontAwesomeIcon
            icon={uploading ? faSpinner : faCloudArrowUp}
            className={`w-8 h-8 text-gray-400 ${uploading ? 'animate-spin' : ''}`}
          />
          <span className="mt-3 block text-sm font-medium text-gray-900 dark:text-white">
            {uploading ? 'Datei wird importiert …' : 'Datei hierher ziehen oder klicken zum Auswählen'}
          </span>
          <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">.xml oder .csv, maximal 16 MB</span>
        </button>

        {uploadError !== null && (
          <p className="mt-3 text-sm text-red-600 dark:text-red-400" role="alert">{uploadError}</p>
        )}

        {uploadResult !== null && <ImportResultPanel result={uploadResult} />}
      </Card>

      <Card title="Import-Verlauf" icon={faClock} padded={false}>
        <DataTable
          bare
          columns={runColumns}
          rows={runs}
          keyExtractor={(run) => run.id}
          loading={status.loading}
          emptyState={
            <EmptyState
              icon={faClock}
              title="Noch keine Importe"
              description="Hier siehst du jeden Abgleich — manuell, automatisch oder per Datei — samt Ergebnis und Meldung."
            />
          }
        />
      </Card>

      <Card title="Importierte Ausführungen" icon={faList} padded={false}>
        <p className="px-4 pt-4 text-sm text-gray-500 dark:text-gray-400">
          Das ist das Rohdaten-Journal von IBKR: Aus mehreren Ausführungen entsteht oft nur ein Trade, deshalb stehen
          hier in der Regel mehr Zeilen als im Trade-Journal.
        </p>
        {executions.error !== null ? (
          <div className="p-4">
            <ErrorState message={executions.error} onRetry={reloadExecutions} />
          </div>
        ) : (
          <>
            <DataTable
              bare
              className="mt-4"
              columns={executionColumns}
              rows={executionRows}
              keyExtractor={(e) => e.id}
              loading={executions.loading}
              emptyState={
                <EmptyState
                  icon={faList}
                  title="Noch keine Ausführungen"
                  description="Nach dem ersten erfolgreichen Import erscheint hier jede einzelne Ausführung aus deiner Flex-Abrechnung."
                />
              }
            />
            <PageBasedPagination
              currentPage={executionPage}
              totalCount={executionTotal}
              pageSize={EXECUTION_PAGE_SIZE}
              onPageChange={setExecutionPage}
              loading={executions.loading}
            />
          </>
        )}
      </Card>

      {configOpen && (
        <ConfigModal config={config} onClose={() => setConfigOpen(false)} onSaved={reloadAll} />
      )}

      <Modal
        isOpen={disconnectOpen}
        onClose={() => setDisconnectOpen(false)}
        title="Verbindung trennen"
        icon={<FontAwesomeIcon icon={faUnlink} className="w-5 h-5 text-red-600 dark:text-red-400" />}
        maxWidth="md"
        closeDisabled={disconnecting}
      >
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Dabei werden dein Flex-Token und die Flex Query ID gelöscht und der automatische Abgleich abgeschaltet.
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Bereits importierte Ausführungen, Trades und Positionen bleiben erhalten — es wird nur der Zugang zu
            Interactive Brokers entfernt. Du kannst ihn jederzeit neu einrichten.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" disabled={disconnecting} onClick={() => setDisconnectOpen(false)}>
              Abbrechen
            </Button>
            <Button variant="danger" loading={disconnecting} onClick={handleDisconnect}>
              Verbindung trennen
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
